/**
 * @category tab
 */
export const PC_BREAK_POINT = 960;

/**
 * @category tab
 * @description 가로 스크롤 drag & drop 기능 이벤트 등록 함수
 * @param {HTMLElement} horizontalScroll 가로 스크롤 되는 영역
 * @returns void
 * @example
 * setHorizontalScrollEvent(document.querySelector('.depth3-list'));
 */
export const setHorizontalSrollEvent = (horizontalScroll: HTMLElement): void => {
    if (!(horizontalScroll instanceof HTMLElement)) return;

    const preventClick = (event: Event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
    };

    let isDown = false;
    let isDragged = false;
    let startX: number;
    let scrollLeft: number;

    const _mousedownEventHandler = (event: MouseEvent, horizontalScroll: HTMLElement) => {
        isDown = true;
        startX = event.pageX - horizontalScroll.offsetLeft;
        scrollLeft = horizontalScroll.scrollLeft;
    };

    const _mouseupEventHandler = (horizontalScroll: HTMLElement) => {
        isDown = false;

        // 마우스 클릭이 끝날 때 a 태그의 기본 이벤트 금지
        if (isDragged) {
            horizontalScroll.childNodes.forEach((childItem) => {
                childItem.addEventListener('click', preventClick);
            });
        } else {
            horizontalScroll.childNodes.forEach((childItem) => {
                childItem.removeEventListener('click', preventClick);
            });
        }

        isDragged = false;
    };

    const _mouseleaveHandler = () => {
        isDown = false;
    };

    const _mousemoveHandler = (event: MouseEvent, horizontalScroll: HTMLElement) => {
        if (!isDown) return;
        event.preventDefault();
        const x = event.pageX - horizontalScroll.offsetLeft;
        const movedX = (x - startX) * 1;
        if (Math.abs(movedX) <= 10) {
            isDragged = false;
        } else {
            isDragged = true;
        }
        horizontalScroll.scrollLeft = scrollLeft - movedX;
    };

    horizontalScroll.addEventListener('mousedown', (event) => _mousedownEventHandler(event, horizontalScroll));
    horizontalScroll.addEventListener('mouseleave', _mouseleaveHandler);
    horizontalScroll.addEventListener('mousemove', (event) => _mousemoveHandler(event, horizontalScroll));
    horizontalScroll.addEventListener('mouseup', () => _mouseupEventHandler(horizontalScroll));
};

/**
 * @category tab
 * @description 화면 크기가 변경될 때 활성화된 탭을 보여주는 이벤트 등록
 * @param {HTMLElement} horizontalScroll 가로 스크롤 되는 영역
 * @param {HTMLElement} activateTab 활성화된 탭
 * @param {boolean} isRTLService RTL 서비스의 적용 유무
 * @example
 * setResizeEvent(document.querySelector('.depth3-list'), document.querySelector('.depth3-list-items.selected'), true);
 */
export const setResizeEvent = (
    horizontalScroll: HTMLElement,
    activatedTab: HTMLElement,
    isRTLService: boolean = false,
    activatedTabIndex: number
): void => {
    if (!(horizontalScroll instanceof HTMLElement) || !(activatedTab instanceof HTMLElement)) return;

    const lastTab = horizontalScroll.lastElementChild;
    if (!(lastTab instanceof HTMLElement)) return;

    const previousActivatedTabX = getTabWidth(activatedTab, activatedTabIndex, 1, isRTLService);

    window.addEventListener('resize', () =>
        _resizeEventHandler(
            lastTab,
            horizontalScroll,
            isRTLService,
            previousActivatedTabX,
            activatedTab,
            activatedTabIndex,
            0,
            0
        )
    );
};

// resize 이벤트 핸들러 함수
const _resizeEventHandler = (
    lastTab: HTMLElement,
    horizontalScroll: HTMLElement,
    isRTLService: boolean = false,
    previousActivatedTabX: number,
    activatedTab: HTMLElement,
    activatedTabIndex: number,
    prevMove: number = 0,
    curMove: number = 0
): void => {
    if (window.innerWidth > PC_BREAK_POINT) return;
    if (!previousActivatedTabX) previousActivatedTabX = getTabWidth(activatedTab, activatedTabIndex, 1, isRTLService);

    const _distanceFromBorder = () => {
        return isRTLService
            ? lastTab.getBoundingClientRect().left
            : lastTab.getBoundingClientRect().right - window.innerWidth;
    };

    const _resizeStartCondition = () => {
        return isRTLService ? _distanceFromBorder() <= 0 : _distanceFromBorder() >= 0;
    };

    if (_resizeStartCondition()) {
        curMove = _distanceFromBorder() - prevMove;

        const _nextMoveCondition = () => {
            const nextMove = horizontalScroll.scrollLeft + curMove;
            return isRTLService ? nextMove <= previousActivatedTabX : nextMove >= previousActivatedTabX;
        };

        if (_nextMoveCondition()) {
            horizontalScroll.scrollLeft = previousActivatedTabX;
        } else {
            horizontalScroll.scrollLeft += curMove;
        }

        prevMove = _distanceFromBorder();
    }
};

/**
 * @category tab
 * @description 활성화된 탭의 N칸 전까지의 너비 구하는 함수 (width)
 * @param {HTMLElement} activatedTab 활성화된 탭
 * @param {number} activatedTabIndex 활성화된 탭 Index
 * @param {number} offset N칸 설정
 * @param {boolean} isRTLService RTL 서비스의 적용 유무
 * @returns {number} N칸 전까지의 탭 크기
 * @example
 * _getTabDistance(document.querySelector('.depth3-list-items.selected'), 3, 2, true);
 */

export const getTabWidth = (
    activatedTab: HTMLElement,
    activatedTabIndex: number,
    offset: number = 1,
    isRTLService: boolean = false
): number => {
    let previousActivatedTabX = 0;
    if (activatedTabIndex - offset >= 0) offset = 1;
    if (window.innerWidth <= PC_BREAK_POINT && activatedTab.parentElement) {
        for (let i = 0; i < activatedTabIndex - offset; i++) {
            previousActivatedTabX += activatedTab.parentElement.children[i].getBoundingClientRect().width;
        }
    }

    return isRTLService ? -1 * previousActivatedTabX : previousActivatedTabX;
};
